import { create } from "zustand";
import { persist } from "zustand/middleware";

import { ChatFilters, ChatWindowState } from "@/types/chat";

import {
  createTenantChangeListener,
  createTenantStorage,
  createUserTenantChangeListener,
  createUserTenantStorage,
  hasUserTenantScope,
} from "./tenant-storage-helpers";

const MAX_ACTIVE_CHATS = 10;

// Collapse windows persisted before the in-flight guard landed: a rapid second
// click added a second placeholder, and both resolved to the same chatId. Keeps
// the frontmost entry, and drops duplicates before capping so they don't eat
// MAX_ACTIVE_CHATS slots.
const normalizeActiveChats = (
  chats: ChatWindowState[] | undefined,
): ChatWindowState[] => {
  if (!Array.isArray(chats)) return [];

  const seen = new Set<string>();
  return chats
    .filter((chat) => {
      if (!chat?.chatId || seen.has(chat.chatId)) return false;
      seen.add(chat.chatId);
      return true;
    })
    .slice(0, MAX_ACTIVE_CHATS);
};

const chatMatches = (
  chat: ChatWindowState,
  identifier: string | ChatWindowState,
): boolean => {
  if (typeof identifier === "string") return chat.chatId === identifier;
  if (chat === identifier) return true;
  if (identifier.localId && chat.localId === identifier.localId) return true;
  return !!identifier.chatId && chat.chatId === identifier.chatId;
};

interface ChatStore {
  // Active chats (tenant-specific)
  activeChats: ChatWindowState[];
  setActiveChats: (chats: ChatWindowState[]) => void;
  addActiveChat: (chat: ChatWindowState) => void;
  removeActiveChat: (identifier: string | ChatWindowState) => void;
  updateActiveChat: (
    identifier: string | ChatWindowState,
    updater: (chat: ChatWindowState) => ChatWindowState,
  ) => void;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      // Active chats
      activeChats: [],
      setActiveChats: (chats) =>
        set({ activeChats: chats.slice(0, MAX_ACTIVE_CHATS) }),
      addActiveChat: (chat) =>
        set((state) => ({
          activeChats: [chat, ...state.activeChats].slice(0, MAX_ACTIVE_CHATS),
        })),
      removeActiveChat: (identifier) =>
        set((state) => ({
          activeChats: state.activeChats.filter(
            (chat) => !chatMatches(chat, identifier),
          ),
        })),
      updateActiveChat: (identifier, updater) =>
        set((state) => ({
          activeChats: state.activeChats.map((chat) =>
            chatMatches(chat, identifier) ? updater(chat) : chat,
          ),
        })),
    }),
    {
      name: "chat-store",
      partialize: (state) => ({
        // Drop in-flight openChatByPatientId placeholders (empty chatId).
        activeChats: state.activeChats
          .filter((chat) => !!chat.chatId)
          .map((chat) => ({
            ...chat,
            isMinimized: true,
            messages: [],
          })),
      }),
      storage: createTenantStorage(),
      version: 4,
      migrate: (persistedState, _version) => {
        const state = persistedState as { activeChats?: ChatWindowState[] };
        return {
          ...state,
          activeChats: normalizeActiveChats(state?.activeChats),
        };
      },
    },
  ),
);

const CHAT_FILTERS_STORE_NAME = "decoda-chat-filters";

const DEFAULT_CHAT_FILTERS: ChatFilters = {
  unreadOnly: false,
  unrespondedOnly: false,
  locationId: undefined,
  humanOnly: false,
  searchTerm: "",
};

// Stored filters are a partial snapshot: `partialize` drops `searchTerm`, and a
// payload written before a new filter was added is missing that key entirely.
// Both the rehydrate and the scope-change paths go through here so neither can
// hand a consumer an undefined filter.
const withFilterDefaults = (
  stored: Partial<ChatFilters> | undefined,
): ChatFilters => ({
  ...DEFAULT_CHAT_FILTERS,
  ...stored,
  searchTerm: "",
});

interface ChatFiltersStore {
  // Chat filters, scoped to the signed-in user within a tenant: the location
  // filter is a saved default, so a shared front-desk browser must not hand one
  // staffer's filter to the next person who signs in.
  chatFilters: ChatFilters;

  // False while `chatFilters` still holds the defaults because the user scope
  // is unresolved. `user-provider` commits `currentUser` inside a flushSync and
  // only then sets the storage uid, so there is a render where the chat
  // surfaces are mounted but the saved location filter has not loaded yet.
  // Fetching there would show every location's conversations for a beat.
  // Seeded from the live scope so a lazily-imported store, which hydrates from
  // storage directly and never sees the listener fire, does not stay stuck.
  isScopeReady: boolean;

  setChatFilters: (filters: ChatFilters) => void;
}

export const useChatFiltersStore = create<ChatFiltersStore>()(
  persist(
    (set) => ({
      chatFilters: DEFAULT_CHAT_FILTERS,
      isScopeReady: hasUserTenantScope(),

      setChatFilters: (filters) =>
        set({
          chatFilters: filters,
        }),
    }),
    {
      name: CHAT_FILTERS_STORE_NAME,
      partialize: (state) => {
        return { chatFilters: { ...state.chatFilters, searchTerm: "" } };
      },
      storage: createUserTenantStorage(),
      merge: (persistedState, currentState) => ({
        ...currentState,
        chatFilters: withFilterDefaults(
          (persistedState as { chatFilters?: Partial<ChatFilters> } | undefined)
            ?.chatFilters,
        ),
        isScopeReady: hasUserTenantScope(),
      }),
      version: 1,
    },
  ),
);

// Listen for tenant changes and load tenant-specific active chats. Always reset
// activeChats so the previous tenant's chats can't leak into the new tenant.
// This path reads localStorage directly, so persist's `migrate` never runs for
// it — normalize here too or a tenant switch reloads the duplicates v4 removed.
createTenantChangeListener("chat-store", (tenantData) => {
  tenantData.activeChats = normalizeActiveChats(tenantData?.activeChats);
  useChatStore.setState(tenantData);
});

// Reload the signed-in user's saved filters when the user or tenant changes.
// Always write a full filter object so an empty scope falls back to the
// defaults instead of keeping the previous user's location filter.
createUserTenantChangeListener(CHAT_FILTERS_STORE_NAME, (userTenantData) => {
  useChatFiltersStore.setState({
    chatFilters: withFilterDefaults(userTenantData?.chatFilters),
    isScopeReady: hasUserTenantScope(),
  });
});
